/**
 * Product category normalization shared by the customer apps (menu filter
 * pills, cart purchase-limit preview) and the API's Colorado purchase-limit
 * axis classification (order-line-deltas). The API stores raw
 * POS/Metrc category names — METRC-style ("Bud Allocated for Extraction",
 * "Pre-Rolls") and, since the Dutchie sync, much more specific retail
 * taxonomy ("Gummy", "Live Rosin", "510 Cartridge", "Shirt", "Dab Tool").
 *
 * Two matching layers, in order:
 *
 * 1. **Category-only term match** (word-boundary against the CATEGORY string
 *    alone — product names are wild and full of strain names like
 *    "Kush Mints", "Candyland", or "Chocolate Thai" that must NOT drag
 *    flower into Edibles):
 *      - accessories terms (shirt, battery, rolling paper, glass, …) → Accessories
 *      - edible terms (gummy, candy, chocolate, mint, drink, …)      → Edibles
 * 2. **Legacy substring rules** (case-insensitive, against
 *    `productCategory + " " + productName` — kept for METRC-style data
 *    where the category is vague and the signal is in the name):
 *      - contains "edible"                           → Edibles
 *      - contains "vape" OR "cart" (incl. cartridge) → Vapes
 *      - contains "concentrate" OR a concentrate
 *        form term (rosin, resin, wax, shatter, …
 *        via category-only word match)               → Concentrate
 *      - everything else                             → Flower (default —
 *        bud, flower, pre-roll/joint, shake, tinctures, topicals, unknowns)
 *
 * Locked 2026-05-06 by user: the four cannabis pills are Concentrate /
 * Flower / Vapes / Edibles; "bud" → Flower and "cart" → Vapes are explicit,
 * and Vapes wins over Concentrate when both signals are present (a Live
 * Resin Cartridge is a Vape product).
 *
 * Locked 2026-09-01 by user: merch stays orderable and gets its own
 * **Accessories** category (it previously fell through to Flower along with
 * Dutchie edible/concentrate names like "Gummy" and "Live Rosin", which
 * this revision also fixes).
 *
 * Display order (filter pills, etc.): Concentrate, Flower, Vapes, Edibles,
 * Accessories.
 */
export const CANONICAL_PRODUCT_CATEGORIES = [
    "Concentrate",
    "Flower",
    "Vapes",
    "Edibles",
    "Accessories",
];
/**
 * Word-boundary match of `terms` against a category string: single-word
 * terms match whole tokens (with a bare trailing-"s" plural), multi-word
 * terms match as a phrase over the normalized token stream. Substring
 * matching is deliberately avoided — "Shatter" contains "hat" and
 * "Batter" is a concentrate form, so loose matching miscategorizes.
 */
function categoryMatchesTerm(category, terms) {
    const tokens = category
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((t) => t.length > 0);
    const joined = tokens.join(" ");
    for (const term of terms) {
        if (term.includes(" ")) {
            if (` ${joined} `.includes(` ${term} `))
                return true;
        }
        else if (tokens.some((t) => t === term || t === `${term}s`)) {
            return true;
        }
    }
    return false;
}
/** Non-cannabis merch/gear — orderable, but never a cannabis pill. */
const ACCESSORY_CATEGORY_TERMS = [
    "accessory",
    "accessories",
    "apparel",
    "ashtray",
    "bag",
    "banger",
    "battery",
    "batteries",
    "beanie",
    "bong",
    "dab tool",
    "gear",
    "glass",
    "grinder",
    "hat",
    "hoodie",
    "keychain",
    "lighter",
    "merch",
    "merchandise",
    "pipe",
    "rolling paper",
    "rolling papers",
    "shirt",
    "shorts",
    "sock",
    "sticker",
    "stash bag",
    "sweatpant",
    "sweatshirt",
    "torch",
    "tote",
    "tray",
    "tee",
];
/** Dutchie-style edible categories that don't contain the word "edible". */
const EDIBLE_CATEGORY_TERMS = [
    "beverage",
    "brownie",
    "candy",
    "candies",
    "caramel",
    "chew",
    "chocolate",
    "cookie",
    "drink",
    "gummy",
    "gummies",
    "lozenge",
    "mint",
    "taffy",
];
/** Dutchie-style concentrate forms that don't contain "concentrate". */
const CONCENTRATE_CATEGORY_TERMS = [
    "badder",
    "batter",
    "budder",
    "crumble",
    "diamond",
    "distillate",
    "hash",
    "kief",
    "resin",
    "rosin",
    "rso",
    "sauce",
    "shatter",
    "sugar wax",
    "wax",
];
export function normalizeProductCategory(productCategory, productName) {
    const category = productCategory ?? "";
    if (categoryMatchesTerm(category, ACCESSORY_CATEGORY_TERMS))
        return "Accessories";
    if (categoryMatchesTerm(category, EDIBLE_CATEGORY_TERMS))
        return "Edibles";
    const hay = `${category} ${productName ?? ""}`.trim().toLowerCase();
    if (hay.includes("edible"))
        return "Edibles";
    if (hay.includes("vape") || hay.includes("cart"))
        return "Vapes";
    if (hay.includes("concentrate") || categoryMatchesTerm(category, CONCENTRATE_CATEGORY_TERMS)) {
        return "Concentrate";
    }
    return "Flower";
}
//# sourceMappingURL=normalize-product-category.js.map